import { Button, ButtonsGroup, Grid, InputText, Toggle } from "#/atoms";
import { FC, memo, useCallback, useState } from "react";
import { Modal, RadioButtonGroup } from "#/molecules";
import { useInput, useToggle } from "@/hooks";

import { DESKTOP_SPACING } from "@/theme";
import { InputHookProps } from "@/hooks/useInput";
import { RadioButtonProps } from "@/components/molecules/RadioButtonGroup/RadioButtonGroup.interface";
import { WorkerModalProps } from "./Worker.interface";

const Worker: FC<WorkerModalProps> = ({ onClose, onSubmit, services }) => {
  const [checkedServices, setCheckedServices] = useState<string[]>([]);

  const email = useInput();
  const firstName = useInput();
  const lastName = useInput();
  const password = useInput();
  const confirmPassword = useInput();
  const phoneNumber = useInput();
  const houseName = useInput();
  const houseNumber = useInput();
  const road = useInput();
  const area = useInput();
  const town = useInput();
  const county = useInput();
  const postcode = useInput();
  const notes = useInput();
  const commutingDistance = useInput();
  const commutingDistanceType = useInput("0");
  const websiteLink = useInput();
  const active = useToggle('active', true);
  const semdEmail = useToggle('sendEmail', false);
  const fullTime = useToggle('fullTime', false);

  
    const commutingDistanceTypes : RadioButtonProps[] = [{
        id: "1",
        label: "Kilometres",
        value: "0"
      },
      {
        id: "2",
        label: "Miles",
        value: "1"
      }
    ]  

    const handleCommutingDistanceTypeChange = (groupName: string, selectedValue: string) => {
      commutingDistanceType.setValue(selectedValue);
    };
  
  const getChecked = (id: string) => {
    return checkedServices.some((s) => s === id);
  }

  const handleCheckedChange = async (id: string, checked: boolean) =>{
    setCheckedServices((prev) =>
      checked ? [...prev, id] : prev.filter((s) => s !== id)
  );
  }

    const validate = useCallback(() => {
      let isValid = true;
  
      if (!firstName.value) {
        isValid = false;
        firstName.setError('First name is missing.');
      } else {
        firstName.setError(null);
      }
  
      if (!lastName.value) {
        isValid = false;
        lastName.setError('Last name is missing.');
      } else {
        lastName.setError(null);
      }
  
      if (!email.value) {
        isValid = false;
        email.setError('Email is incorrect.');
      } else {
        email.setError(null);
      }
  
      if (password.value?.length < 6) {
        isValid = false;
        password.setError('Password is too short.');
      } else {
        password.setError(null);
      }
  
      if (confirmPassword.value?.length < 6) {
        isValid = false;
        confirmPassword.setError('Password is too short.');
      } else {
        confirmPassword.setError(null);
      }
  
      if (password.value !== confirmPassword.value) {
        isValid = false;
        confirmPassword.setError('Passwords do not match.');
      } else {
        confirmPassword.setError(null);
      }
  
      return isValid;
    }, [firstName.value, lastName.value, email.value, password.value, confirmPassword.value]);
    
      const onChangeValue = (e : React.ChangeEvent<HTMLInputElement>, input: InputHookProps) => {
        input.onChange(e);
      }

  const handleCreate = useCallback(() => {
    if (!validate()) {
      return;
    }

    onSubmit({
      active: active.checked,
      area: area.value,
      commutingDistance: commutingDistance.value,
      commutingDistanceType: commutingDistanceType.value,
      county: county.value,
      email: email.value,
      firstName: firstName.value,
      fullTime: fullTime.checked,
      houseName: houseName.value,
      houseNumber: houseNumber.value,
      lastName: lastName.value,
      notes: notes.value,
      password: password.value,
      phoneNumber: phoneNumber.value,
      postcode: postcode.value,
      road: road.value,
      sendEmail: semdEmail.checked,
      services: checkedServices,
      town: town.value,
      websiteLink: websiteLink.value,
      workerTypeId: '',
    })
  }, [active, confirmPassword, email, firstName, fullTime, houseName, houseNumber, lastName, password, phoneNumber, road, area, town, county, postcode, notes]);

  return (
    <Modal onClose={onClose} title="Create New Worker" width="650px">
      <InputText type="text" label="First Name" value={firstName.value} onChange={(e) => onChangeValue(e, firstName)} errorMessage={firstName.error || ''} state={firstName.error ? 'error' : 'idle'} />
      <InputText type="text" label="Last Name" value={lastName.value} onChange={(e) => onChangeValue(e, lastName)} errorMessage={lastName.error || ''} state={lastName.error ? 'error' : 'idle'} />
      <InputText type="text" label="E-mail Address" value={email.value} onChange={(e) => onChangeValue(e, email)} errorMessage={email.error || ''} state={email.error ? 'error' : 'idle'} />
      <InputText type="password" label="Password" value={password.value} onChange={(e) => onChangeValue(e, password)}  errorMessage={password.error || ''} state={password.error ? 'error' : 'idle'} />
      <InputText type="password" label="Confirm Password" value={confirmPassword.value} onChange={(e) => onChangeValue(e, confirmPassword)} errorMessage={confirmPassword.error || ''} state={confirmPassword.error ? 'error' : 'idle'} />

      <InputText type="text" label="Phone Number" value={phoneNumber.value} onChange={phoneNumber.onChange} />
      <InputText type="text" label="House Name" value={houseName.value} onChange={houseName.onChange} />
      <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 50%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
        <InputText type="text" label="Number" value={houseNumber.value} onChange={houseNumber.onChange} />
        <InputText type="text" label="Road" value={road.value} onChange={road.onChange} />
        <InputText type="text" label="Area" value={area.value} onChange={area.onChange} />
        <InputText type="text" label="Town" value={town.value} onChange={town.onChange} />
        <InputText type="text" label="County" value={county.value} onChange={county.onChange} />
        <InputText type="text" label="Postcode" value={postcode.value} onChange={postcode.onChange} />
      </Grid>
      <InputText type="text" label="Notes" value={notes.value} onChange={notes.onChange} />
        <InputText type="number" label="Commuting Distance" value={commutingDistance.value}  onChange={commutingDistance.onChange} />
        <RadioButtonGroup groupName="cancelFeeType" inputs={commutingDistanceTypes} selectedValue={commutingDistanceType.value} onChangeValue={handleCommutingDistanceTypeChange} />

        <InputText type="text" label="Website Link" value={websiteLink.value}  onChange={websiteLink.onChange} />

      <Grid rowGap={DESKTOP_SPACING.XXL} gridTemplateColumns="repeat(1, 100%)">
        <Toggle checked={active.checked} id={active.value} label="Active" labelPosition="left" onToggle={active.onChange} />
        <Toggle checked={semdEmail.checked} id={semdEmail.value} label="Send e-mail notification" labelPosition="left" onToggle={semdEmail.onChange} />
        <hr/>
        <Toggle checked={fullTime.checked} id={fullTime.value} label="Full Time Worker" labelPosition="left" onToggle={fullTime.onChange} />
        <hr />
        { services.map((service) => (<Toggle 
                      id={service.id}
                      key={service.id} 
                      label={service.name} 
                      checked={getChecked(service.id)} 
                      onToggle={(checked) => { handleCheckedChange(service.id, checked) }}
                     />) ) }
      </Grid>
      <ButtonsGroup fullWidth margin={`${DESKTOP_SPACING.EL}px 0 0`}>
        <Button onClick={handleCreate}>Create</Button>
      </ButtonsGroup>
    </Modal>
  );
};

export default memo(Worker);
